import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormCheckbox from 'components/KNForm/KNFormCheckbox'
import { analyticsEvent } from 'global/helpers/analytics'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { booleanFormValuesToEnum, enumToBooleanFormValues } from './CarrierProfile.helpers'
import { CARRIER_PROFILE_QUERY_KEY, editCarrierProfile } from './CarrierProfile.service'
import { type CarrierProfile, ProfileType } from './CarrierProfile.types'

interface SelectTypesDialogPayload {
  readonly carrierProfile: CarrierProfile,
}

export interface SelectTypesDialogProps {
  readonly payload: SelectTypesDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

export type TypesFormValues = {
  [K in ProfileType]: boolean
}

const SelectTypesDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: SelectTypesDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<TypesFormValues>()
  const allTypes = Object.values(ProfileType)
  const queryClient = useQueryClient()
  const updateCarrierProfileMutation = useMutation({
    mutationFn: async (data: TypesFormValues) => {
      await editCarrierProfile({
        ...payload.carrierProfile,
        types: booleanFormValuesToEnum<TypesFormValues, ProfileType>(data),
      })
    },
    onError: (error) => setError('root', processServerErrorMessages(error)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CARRIER_PROFILE_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues(
          enumToBooleanFormValues<ProfileType, TypesFormValues>(allTypes, payload.carrierProfile.types),
        ),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<TypesFormValues> = async (data: TypesFormValues): Promise<void> => {
    await updateCarrierProfileMutation.mutateAsync(data)
    onAction()
    analyticsEvent('polestar_carrier_profile_types_updated')
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={t('screens.cs.carrier_profile.company.select_types')}
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t('general.save')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack direction="column" spacing={1}>
        {allTypes.map((type) => (
          <KNFormCheckbox
            key={type}
            name={type}
            control={control}
            label={t(`screens.cs.carrier_profile.types.${type}`)}
          />
        ))}
      </Stack>
    </KNDialog>
  )
}

export default SelectTypesDialog
